<script lang="ts">
  import { 
    X, Award, Droplets, AlertTriangle, CheckCircle2, 
    TrendingUp, TrendingDown, ArrowRight, ExternalLink, 
    Calculator, HelpCircle, Activity, Sparkles, ShieldAlert,
    Gauge, Info
  } from "lucide-svelte";

  let { 
    isOpen = false, 
    onClose = () => {}, 
    benchmark = null, 
    property = null,
    totalUnits = 0,
    activeAlertsCount = 0
  } = $props<{
    isOpen: boolean;
    onClose: () => void;
    benchmark?: any;
    property?: any;
    totalUnits?: number;
    activeAlertsCount?: number;
  }>();

  // Derived metrics
  const percentile = $derived(
    benchmark?.portfolio_benchmark_percentile ?? property?.portfolio_benchmark_percentile ?? 42
  );
  const actualGUD = $derived(
    benchmark?.gallons_per_unit_day ?? 188.5
  );
  const region = $derived(
    benchmark?.region_market ?? 'Southern California'
  );
  const variancePct = $derived(
    benchmark?.monthly_budget_variance_pct ?? 18.5
  );
  const alertsCount = $derived(
    benchmark?.active_anomaly_count ?? activeAlertsCount ?? 0
  );
  const propertyName = $derived(
    property?.property_name ?? benchmark?.property_name ?? 'Property'
  );
  const unitsCount = $derived(
    totalUnits || property?.total_units || benchmark?.total_units || 45
  );

  // Baseline target calculated from actual demand and variance
  const baselineGUD = $derived(
    variancePct !== -100 ? +(actualGUD / (1 + variancePct / 100)).toFixed(1) : 145.0
  );

  // Leak drag: estimated 9.3 GUD per unresolved leak
  const leakDragGUD = $derived(
    +(alertsCount * 9.3).toFixed(1)
  );

  // Post-repair projection
  const postRepairGUD = $derived(
    +Math.max(actualGUD - leakDragGUD, baselineGUD * 0.95).toFixed(1)
  );
  const postRepairPercentile = $derived(
    alertsCount > 0 ? Math.min(Math.round(percentile + (alertsCount * 7.5)), 74) : percentile
  );

  // Rating bracket
  const ratingTier = $derived.by(() => {
    if (percentile >= 75) {
      return {
        label: 'Optimal Efficiency',
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
        badge: 'badge-success text-white',
        desc: 'Eligible for national EPA WaterScore recognition and top-tier ESG certification.'
      };
    }
    if (percentile >= 60) {
      return {
        label: 'Operational Advisory',
        color: 'text-warning',
        bg: 'bg-warning/10 border-warning/30 text-warning',
        badge: 'badge-warning text-base-content font-bold',
        desc: 'Moderate consumption variance relative to regional multifamily peers. Room for fixture optimization.'
      };
    }
    return {
      label: 'Critical Alarm',
      color: 'text-error',
      bg: 'bg-error/10 border-error/30 text-error',
      badge: 'badge-error text-white font-bold',
      desc: 'Elevated water draw and active leak anomalies placing the property in the lower efficiency cohort.'
    };
  });

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if isOpen}
  <!-- Backdrop -->
  <div 
    class="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto animate-fadeIn"
    role="dialog"
    aria-modal="true"
    aria-labelledby="epa-modal-title"
    onclick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div 
      class="bg-base-100 rounded-3xl border border-base-200/80 shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden text-base-content"
    >
      <!-- Modal Header -->
      <div class="p-5 sm:p-6 border-b border-base-200/80 flex items-center justify-between bg-base-200/40">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-2xl bg-primary/10 text-primary">
            <Award class="w-6 h-6" />
          </div>
          <div>
            <div class="flex items-center gap-2 flex-wrap">
              <h2 id="epa-modal-title" class="text-lg sm:text-xl font-black tracking-tight">
                EPA WaterScore™ Calculation & Pacing
              </h2>
              <span class="badge badge-outline badge-sm font-bold uppercase tracking-wider text-[10px]">
                ENERGY STAR® Standard
              </span>
            </div>
            <p class="text-xs text-base-content/60 mt-0.5">
              Multifamily property water efficiency benchmarking for <strong class="text-base-content/90">{propertyName}</strong>
            </p>
          </div>
        </div>

        <button 
          onclick={onClose}
          class="btn btn-sm btn-circle btn-ghost text-base-content/60 hover:text-base-content"
          aria-label="Close modal"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Modal Body (Scrollable) -->
      <div class="p-5 sm:p-6 overflow-y-auto space-y-6">
        
        <!-- Score & Spectrum Banner -->
        <div class="rounded-2xl border {ratingTier.bg} p-5 space-y-4">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div class="flex items-center gap-2">
                <span class="text-3xl sm:text-4xl font-black font-mono {ratingTier.color}">
                  {percentile}th %ile
                </span>
                <span class="badge shrink-0 whitespace-nowrap {ratingTier.badge}">
                  {ratingTier.label}
                </span>
              </div>
              <p class="text-xs text-base-content/70 mt-1 max-w-xl">
                {ratingTier.desc}
              </p>
            </div>
            <div class="text-left sm:text-right border-t sm:border-t-0 pt-2 sm:pt-0 border-base-content/10">
              <span class="text-[11px] font-semibold text-base-content/50 uppercase tracking-wider block">Climate Cohort</span>
              <span class="text-xs font-bold text-base-content/80">{region} Multifamily</span>
            </div>
          </div>

          <!-- 1-100 Percentile Spectrum Bar -->
          <div class="space-y-1.5 pt-2">
            <div class="flex justify-between text-[11px] font-bold text-base-content/60">
              <span>0 (Lowest)</span>
              <span class="text-error">60 Threshold</span>
              <span class="text-warning">75 Optimal</span>
              <span class="text-success">100 (Top)</span>
            </div>

            <!-- Gradient Track -->
            <div class="relative h-4 rounded-full bg-linear-to-r from-red-500 via-amber-400 to-emerald-500 p-0.5 shadow-inner">
              <!-- Score Marker Needle -->
              <div 
                class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-700"
                style="left: {Math.min(Math.max(percentile, 4), 96)}%;"
              >
                <div class="w-6 h-6 rounded-full bg-base-100 border-2 border-primary shadow-lg flex items-center justify-center font-mono text-[9px] font-black text-primary">
                  {percentile}
                </div>
              </div>
            </div>

            <!-- Context note under spectrum -->
            <div class="flex items-center justify-between text-[11px] text-base-content/50 pt-1">
              <span>◄ 58% of peer properties are more water-efficient</span>
              <span>Top 25% earn EPA Certification ►</span>
            </div>
          </div>
        </div>

        <!-- Formula Breakdown Section -->
        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <Calculator class="w-4 h-4 text-primary" />
            <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/80">
              How the Score is Calculated
            </h3>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- Metric 1: Actual Demand (GUD) -->
            <div class="bg-base-200/50 rounded-2xl p-4 border border-base-200 space-y-1">
              <div class="flex items-center justify-between text-xs font-semibold text-base-content/60">
                <span>Actual Demand (GUD)</span>
                <Droplets class="w-3.5 h-3.5 text-primary" />
              </div>
              <div class="text-2xl font-black font-mono text-base-content">
                {actualGUD}
                <span class="text-xs font-normal text-base-content/60">gal/unit/day</span>
              </div>
              <p class="text-[11px] text-base-content/60 leading-tight">
                <strong>{Math.round(actualGUD * unitsCount * 30).toLocaleString()} gal/mo</strong> whole-building draw ({Math.round(actualGUD * unitsCount).toLocaleString()} gal/day across {unitsCount} units).
              </p>
            </div>

            <!-- Metric 2: Climate Target -->
            <div class="bg-base-200/50 rounded-2xl p-4 border border-base-200 space-y-1">
              <div class="flex items-center justify-between text-xs font-semibold text-base-content/60">
                <span>Regional Baseline</span>
                <Gauge class="w-3.5 h-3.5 text-emerald-500" />
              </div>
              <div class="text-2xl font-black font-mono text-emerald-600 dark:text-emerald-400">
                {baselineGUD}
                <span class="text-xs font-normal text-base-content/60">gal/unit/day</span>
              </div>
              <p class="text-[11px] text-base-content/50 leading-tight">
                EPA Portfolio Manager expected baseline for {region} weather & units.
              </p>
            </div>

            <!-- Metric 3: Variance & Leaks -->
            <div class="bg-base-200/50 rounded-2xl p-4 border border-base-200 space-y-1">
              <div class="flex items-center justify-between text-xs font-semibold text-base-content/60">
                <span>Variance vs Target</span>
                {#if variancePct > 0}
                  <TrendingUp class="w-3.5 h-3.5 text-error" />
                {:else}
                  <TrendingDown class="w-3.5 h-3.5 text-success" />
                {/if}
              </div>
              <div class="text-2xl font-black font-mono {variancePct > 0 ? 'text-error' : 'text-success'}">
                {variancePct > 0 ? '+' : ''}{variancePct}%
              </div>
              <p class="text-[11px] text-base-content/50 leading-tight">
                {#if alertsCount > 0}
                  <strong class="text-error">{alertsCount} active leak {alertsCount === 1 ? 'alert' : 'alerts'}</strong> adding ~{leakDragGUD} GUD in unmitigated loss.
                {:else}
                  Operating close to baseline expectations.
                {/if}
              </p>
            </div>
          </div>
        </div>

        <!-- Path to Score Improvement (Simulation) -->
        <div class="bg-base-200/30 rounded-2xl p-5 border border-base-200/80 space-y-3">
          <div class="flex items-center gap-2">
            <Sparkles class="w-4 h-4 text-amber-500" />
            <h3 class="text-sm font-bold uppercase tracking-wider text-base-content/80">
              Pacing & Score Improvement Roadmap
            </h3>
          </div>

          <div class="space-y-2.5">
            <!-- Step 1: Current State -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 p-3 rounded-xl bg-base-100 border border-base-content/10 text-xs">
              <div class="flex items-center gap-2.5 min-w-0">
                <span class="badge badge-sm badge-error text-white font-bold shrink-0 whitespace-nowrap">Current</span>
                <div class="min-w-0">
                  <span class="font-bold">Active Operation:</span> {actualGUD} GUD with {alertsCount} open leak {alertsCount === 1 ? 'anomaly' : 'anomalies'}
                </div>
              </div>
              <span class="font-mono font-bold text-error shrink-0 whitespace-nowrap">{percentile}th %ile</span>
            </div>

            <!-- Step 2: Resolve Active Alerts -->
            {#if alertsCount > 0}
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 p-3 rounded-xl bg-base-100 border border-base-content/10 text-xs">
                <div class="flex items-center gap-2.5 min-w-0">
                  <span class="badge badge-sm badge-warning font-bold shrink-0 whitespace-nowrap">Step 1</span>
                  <div class="min-w-0">
                    <span class="font-bold">Resolve {alertsCount} Active Leaks:</span> Recovers ~{leakDragGUD} GUD (brings draw to ~{postRepairGUD} GUD)
                  </div>
                </div>
                <div class="flex items-center gap-1.5 font-mono font-bold text-warning shrink-0 whitespace-nowrap">
                  <span>{postRepairPercentile}th %ile</span>
                  <ArrowRight class="w-3 h-3 inline text-base-content/40" />
                </div>
              </div>
            {/if}

            <!-- Step 3: Fixture Optimization -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 p-3 rounded-xl bg-base-100 border border-base-content/10 text-xs">
              <div class="flex items-center gap-2.5 min-w-0">
                <span class="badge badge-sm badge-success text-white font-bold shrink-0 whitespace-nowrap">Target</span>
                <div class="min-w-0">
                  <span class="font-bold">Fixture Acoustic Optimization:</span> Reach baseline &lt;{baselineGUD} GUD via flapper/aerator tune-ups
                </div>
              </div>
              <span class="font-mono font-bold text-emerald-600 dark:text-emerald-400 shrink-0 whitespace-nowrap">82nd %ile (Optimal)</span>
            </div>
          </div>
        </div>

        <!-- EPA Methodology & Regulatory Standard Notes -->
        <div class="bg-base-200/20 rounded-2xl p-4 border border-base-200/60 text-xs text-base-content/70 space-y-2">
          <div class="flex items-center gap-1.5 font-bold text-base-content">
            <Info class="w-3.5 h-3.5 text-primary" />
            <span>EPA ENERGY STAR® Portfolio Manager Standard</span>
          </div>
          <p class="leading-relaxed text-[11.5px]">
            The EPA 1–100 WaterScore evaluates multifamily properties against national commercial water survey data (CBECS / RECS), normalizing for property size, unit count, climate degree-days, and local weather. Properties scoring <strong>75 or higher</strong> are recognized by the US EPA as top water stewards and qualify for LEED WE and GRESB ESG certifications.
          </p>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="p-4 sm:p-5 border-t border-base-200/80 bg-base-200/30 flex items-center justify-between">
        <a 
          href="https://www.epa.gov/watersense/water-score-multifamily-housing" 
          target="_blank" 
          rel="noopener noreferrer"
          class="text-xs text-primary hover:underline flex items-center gap-1 font-semibold"
        >
          EPA WaterSense & ENERGY STAR® WaterScore Guide <ExternalLink class="w-3 h-3" />
        </a>

        <button 
          onclick={onClose}
          class="btn btn-sm btn-primary rounded-xl px-5"
        >
          Done
        </button>
      </div>

    </div>
  </div>
{/if}

<style>
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }
  .animate-fadeIn {
    animation: fadeIn 0.15s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
</style>
